export type SummaryBenefit = {
  type: string;
  network: string;
  notes: string[];
};
export type SummaryResult = {
  mode: string;
  failure?: string;
  errors: { message: string }[];
  coverage: { statusCode?: string }[];
  benefits: SummaryBenefit[];
};
export type VisitScenario = { codes: string; setting: string; cost: string };
export const visitSettings: Record<string, string> = {
  other: 'Other setting',
  office: 'In-person office visit',
  unspecified: 'Setting not specified',
  audio: 'Telephone / audio-only visit',
  video_home: 'Video visit — patient at home',
  video_elsewhere: 'Video visit — patient elsewhere',
  telemedicine: 'Telemedicine — format / patient location not specified',
};
export function summarizeVisit(result: SummaryResult, visit: VisitScenario) {
  const cost = visit.cost.trim();
  const validCost = /^\d+(\.\d{1,2})?$/.test(cost) && Number.isFinite(Number(cost));
  const amount = validCost
    ? new Intl.NumberFormat('en-US', { currency: 'USD', style: 'currency' }).format(Number(cost))
    : null;
  const scenario = `${visit.codes.trim() ? `CPT/HCPCS ${visit.codes.trim().toUpperCase()}` : 'Procedure codes not entered'} · ${visitSettings[visit.setting] || visitSettings.unspecified}${amount ? ` · Total charge ${amount}` : ''}`;
  if (result.mode === 'test')
    return {
      followUp: [],
      scenario: 'Synthetic example only',
      title: 'Sample benefits — no personal cost estimate',
      budget: 'Run a real coverage check to review your benefits.',
      finding: 'These benefits belong to a sample patient and cannot be used to estimate your visit cost.',
    };
  const issues = Boolean(result.failure || result.errors.length);
  const active = result.coverage.some((c) => c.statusCode === '1');
  // Only explicitly out-of-network rows count. Unspecified/not-applicable network
  // indicators and in-network zero-dollar rows cannot establish OON coverage.
  const oon = result.benefits.some((b) => b.network === 'Out of network');
  const grace = result.benefits.some((b) => b.notes.some((n) => /grace\s+period/i.test(n)));
  return {
    scenario,
    title: issues ? 'Coverage check needs attention' : 'Out-of-network reimbursement is not yet confirmed',
    budget: amount
      ? `For planning, be prepared to pay the full ${amount}. The insurer’s payment is unknown; this is a budgeting precaution, not a confirmed amount owed or a denial.`
      : 'Enter a valid total anticipated charge to frame your budget. The insurer’s payment is currently unknown.',
    finding: issues
      ? 'The insurer returned an issue. These findings cannot establish coverage or payment for this visit.'
      : `${active ? 'The insurer reports active coverage, but that does not confirm payment for this visit.' : 'Active coverage was not confirmed in the returned plan status.'} ${oon ? 'Some out-of-network benefits were reported; their applicability to these procedures and this setting still needs confirmation.' : 'No explicitly labeled out-of-network benefits were returned. Missing benefits are not a confirmed denial.'}`,
    followUp: [
      'Confirm coverage for each procedure with the actual treating clinician, service date, and visit setting, including any out-of-network exception or authorization.',
      'Ask for the allowed amount (the amount the insurer recognizes), the applicable remaining deductible, copay, and coinsurance. A $0 in-network copay does not establish free out-of-network care.',
      ...(visit.setting.startsWith('video') || visit.setting === 'audio' || visit.setting === 'telemedicine'
        ? [
            'Confirm telehealth requirements. Coverage for a virtual visit does not by itself confirm coverage for an out-of-network clinician.',
          ]
        : []),
      ...(grace
        ? [
            'The response mentions a grace period. Ask the insurer whether this affects payment for the planned service date.',
          ]
        : []),
    ],
  };
}
